'use client';

// ChatInterface - Main chat interface component with WhatsApp-style layout

import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import type { ChatMessage as ChatMessageType } from './types';

export interface ChatInterfaceProps {
  /** Messages to display */
  messages: ChatMessageType[];
  /** Callback when user sends a message */
  onSendMessage: (content: string) => void;
  /** Whether a message is being sent */
  isLoading?: boolean;
  /** Error message to display */
  error?: Error | null;
  /** Whether to show typing indicator */
  showTypingIndicator?: boolean;
  /** Initial input value (for suggested questions) */
  inputValue?: string;
  /** Callback when input value changes */
  onInputChange?: (value: string) => void;
}

/**
 * TypingIndicator - Three animated dots showing bot is thinking
 */
function TypingIndicator() {
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isRtl ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          py: 1.5,
          backgroundColor: 'grey.100',
          borderRadius: isRtl ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
          border: '1px solid',
          borderColor: 'grey.200',
          width: 'fit-content',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'grey.400',
              animation: 'bounce 1.4s ease-in-out infinite',
              animationDelay: `${i * 0.16}s`,
              '@keyframes bounce': {
                '0%, 80%, 100%': { transform: 'translateY(0)' },
                '40%': { transform: 'translateY(-6px)' },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * ChatInterface - Main chat interface component
 *
 * Features:
 * - WhatsApp-style container with subtle background
 * - Message list with auto-scroll to latest
 * - Animated typing indicator (three bouncing dots)
 * - Empty state message
 * - Error display
 * - Input fixed at bottom
 * - Mobile-responsive layout (max 85% bubble width)
 * - RTL support
 */
export function ChatInterface({
  messages,
  onSendMessage,
  isLoading = false,
  error = null,
  showTypingIndicator = false,
  inputValue = '',
  onInputChange,
}: ChatInterfaceProps) {
  const t = useTranslations('creator.testing');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive (within chat container only)
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, showTypingIndicator]);

  const isEmpty = messages.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: { xs: 'calc(100dvh - 200px)', md: 600 },
        minHeight: { xs: 250, md: 400 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      {/* Messages area */}
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: 'grey.50',
        }}
      >
        {/* Empty state */}
        {isEmpty && !isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 200,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {t('messages.noMessages')}
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Typing indicator - animated dots styled like bot message */}
        {showTypingIndicator && !messages.some((m) => m.isStreaming) && (
          <TypingIndicator />
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error.message || t('errors.sendFailed')}
        </Alert>
      )}

      {/* Input area - fixed at bottom with white background */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'white',
        }}
      >
        <ChatInput
          onSend={onSendMessage}
          disabled={false}
          isLoading={isLoading}
          initialValue={inputValue}
          {...(onInputChange ? { onChange: onInputChange } : {})}
        />
      </Box>
    </Box>
  );
}

export default ChatInterface;
